import { __read, __spreadArray, __values } from "tslib";
import { QueryAggregateType } from '@tencent/memoire-core';
import { QueryTreeNodeType, } from './query-tree';
var UNION = QueryTreeNodeType.UNION, INTERSECT = QueryTreeNodeType.INTERSECT, HYDRATE = QueryTreeNodeType.HYDRATE, INDEX_SCAN = QueryTreeNodeType.INDEX_SCAN, SCAN = QueryTreeNodeType.SCAN, SCAN_ALL = QueryTreeNodeType.SCAN_ALL, FILTER = QueryTreeNodeType.FILTER;
// QueryOptimizer optimizes query plan tree based on optimizing rules
var QueryOptimizer = /** @class */ (function () {
    function QueryOptimizer() {
    }
    // optimizing query plan based on rules
    QueryOptimizer.prototype.optimize = function (plan) {
        var rules = [
            OptimizeIndices,
            MergeScanAndHydrate,
            LinkIntervalFilters,
            MergeSingleIntersectOrUnion,
            PushUpIntersectMemFilter,
            PushDownTopN,
            // hotfix: apply twice to ensure scan and hydrate are merged
            // TODO: maybe there are better algorithms
            MergeSingleIntersectOrUnion,
            MergeScanAndHydrate,
            ForceScanValues,
        ];
        return this.applyAllRules(rules, plan);
    };
    // apply optimize rules one by one
    QueryOptimizer.prototype.applyAllRules = function (rules, plan) {
        return rules.reduce(function (plan, rule) { return rule(plan); }, plan);
    };
    return QueryOptimizer;
}());
// if the plan has pattern [(Intersect|Union)/SingleNode]
// transform to [SingleNode]
function MergeSingleIntersectOrUnion(plan) {
    mergeSingleIntersectOrUnion(plan.root);
    return plan;
}
function mergeSingleIntersectOrUnion(parent) {
    var e_1, _a;
    var children = parent.children;
    try {
        for (var _b = __values(children.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), i = _d[0], child = _d[1];
            mergeSingleIntersectOrUnion(child);
            if ([INTERSECT, UNION].includes(child.type) && child.children.length === 1) {
                var innerNode = child.children[0];
                children[i] = innerNode;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
}
// if the plan has pattern [Scan*/Hydrate] or [Hydrate/Scan*]
// transform to [Scan*(hydrate=true)]
function MergeScanAndHydrate(plan) {
    mergeScanAndHydrate(plan.root);
    return plan;
}
function mergeScanAndHydrate(parent) {
    var e_2, _a, e_3, _b;
    var children = parent.children;
    try {
        for (var _c = __values(children.entries()), _d = _c.next(); !_d.done; _d = _c.next()) {
            var _e = __read(_d.value, 2), i = _e[0], child = _e[1];
            mergeScanAndHydrate(child);
            if (child.type !== HYDRATE)
                continue;
            var didScan = false;
            try {
                for (var _f = (e_3 = void 0, __values(child.children)), _g = _f.next(); !_g.done; _g = _f.next()) {
                    var innerNode = _g.value;
                    if (innerNode.type === SCAN || innerNode.type === INDEX_SCAN || innerNode.type === SCAN_ALL) {
                        didScan = true;
                        innerNode.hydrate = true;
                    }
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
                }
                finally { if (e_3) throw e_3.error; }
            }
            if (didScan && child.children.length === 1) {
                var innerNode = child.children[0];
                children[i] = innerNode;
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_2) throw e_2.error; }
    }
}
// if the plan has pattern [Paginate(offset,limit)/Sort/**/Scan*]
// transform to TopN request and push down to [Scan*] node based on the intermediate nodes
function PushDownTopN(plan) {
    // TODO
    return plan;
}
// if the plan has pattern [Intersect/(MemFilter~MemFilter)] or [Union/(MemFilter~MemFilter)]
// link all filter into one based on parent node
function LinkIntervalFilters(plan) {
    linkIntervalFilters(plan.root);
    return plan;
}
function linkIntervalFilters(parent) {
    var head;
    var current;
    removeInPlace(parent.children, function (child) {
        linkIntervalFilters(child);
        if (child.type !== FILTER || !hasScanAllChildren(child)) {
            return true;
        }
        if (!current) {
            head = child;
        }
        else {
            var nextFilter = child;
            mergeFilters(current, nextFilter);
            nextFilter.regroup = parent.type === UNION;
        }
        current = child;
        return false;
    });
    if (head) {
        parent.children.push(head);
    }
}
function mergeFilters(head, next) {
    var current = head;
    while (head.nextFilter) {
        current = head.nextFilter;
    }
    current.nextFilter = next;
    return head;
}
/**
 * Remove items from a list by a filter function, and return the removed items
 *
 * @param list a list of items to filter
 * @param filter a filter function to determine which item to keep
 * @returns the items to be removed
 */
function removeInPlace(list, filter) {
    var items = list;
    var i = 0;
    var j = items.length - 1;
    while (i <= j) {
        var item = items[i];
        if (filter(item)) {
            i += 1;
            continue;
        }
        // move filtered items to tail
        items[i] = items[j];
        items[j] = item;
        j -= 1;
    }
    // remove tail items
    return items.splice(j + 1);
}
function hasScanAllChildren(node) {
    return node.children.some(function (node) { return node.type === SCAN_ALL; });
}
// if the plan has pattern [Intersect/(MemFilter + Scan*)]
// transform to [MemFilter/Intersect/Scan*] to use index / key as much as possible
function PushUpIntersectMemFilter(plan) {
    pushUpIntersectMemFilter(plan.root);
    return plan;
}
function pushUpIntersectMemFilter(parent) {
    var e_4, _a;
    var children = parent.children;
    try {
        for (var _b = __values(children.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), i = _d[0], child = _d[1];
            pushUpIntersectMemFilter(child);
            if (child.type !== INTERSECT) {
                continue;
            }
            var siblingScanNode = child.children.find(function (_a) {
                var type = _a.type;
                return [SCAN, INDEX_SCAN].includes(type);
            });
            if (!siblingScanNode) {
                continue;
            }
            var filterNodes = removeInPlace(child.children, function (innerNode) { return innerNode.type !== FILTER; });
            if (!filterNodes.length) {
                continue;
            }
            var hydrateNode = {
                type: HYDRATE,
                collectionSchema: siblingScanNode.collectionSchema,
                children: [],
            };
            children[i] = composeNodes.apply(void 0, __spreadArray(__spreadArray([], __read(filterNodes)), [hydrateNode, child]));
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_4) throw e_4.error; }
    }
}
// hotfix: force all scan node to load values directly
function ForceScanValues(plan) {
    // skip optimization when request is keys or count
    if ([QueryAggregateType.COUNT, QueryAggregateType.KEYS].includes(plan.aggregate)) {
        return plan;
    }
    traverseBottomUp(function (node) {
        // recursively update all scan node hydrate=true
        if (node.type === SCAN || node.type === INDEX_SCAN) {
            var hydratedNode = node;
            hydratedNode.hydrate = true;
        }
    }, plan.root);
    return plan;
}
// choose the most efficient index to scan when perform the query execution
function OptimizeIndices(plan) {
    traverseBottomUp(function (node) {
        var e_5, _a, e_6, _b;
        var _c;
        if (node.type !== INTERSECT)
            return;
        // get the maxPriority of all child scan nodes
        var maxPriority = Number.MIN_SAFE_INTEGER;
        try {
            for (var _d = __values(node.children), _e = _d.next(); !_e.done; _e = _d.next()) {
                var child = _e.value;
                var priority = Number.MIN_SAFE_INTEGER;
                if (child.type === SCAN) {
                    // force the priority of scanning a key range in main table to be zero
                    priority = 0;
                }
                if (child.type === INDEX_SCAN && child.index.priority) {
                    // use index priority
                    priority = child.index.priority;
                }
                // if the index node has no priority, then set to min
                maxPriority = Math.max(maxPriority, priority);
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_5) throw e_5.error; }
        }
        var children = node.children;
        try {
            for (var _f = __values(children.entries()), _g = _f.next(); !_g.done; _g = _f.next()) {
                var _h = __read(_g.value, 2), index = _h[0], child = _h[1];
                if (child.type !== INDEX_SCAN)
                    continue;
                var priority = (_c = child.index.priority) !== null && _c !== void 0 ? _c : Number.MIN_SAFE_INTEGER;
                if (priority < maxPriority) {
                    // any nodes has lower priority than maxPriority will become a mem filter node
                    children[index] = becomeMemFilter(child);
                }
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
            }
            finally { if (e_6) throw e_6.error; }
        }
    }, plan.root);
    return plan;
}
// transform a scan node to mem filter node
function becomeMemFilter(node) {
    return {
        path: node.index.path,
        intervals: node.intervals,
        comparer: node.comparer,
        type: QueryTreeNodeType.FILTER,
        regroup: false,
        children: [
            {
                type: QueryTreeNodeType.SCAN_ALL,
                collectionSchema: node.collectionSchema,
                comparer: node.comparer,
                hydrate: true,
                children: [],
            },
        ],
    };
}
// traverse all nodes recursively in post order (bottom to up)
function traverseBottomUp(operation, current, parent, index) {
    var e_7, _a;
    if (parent === void 0) { parent = null; }
    if (index === void 0) { index = -1; }
    var children = current.children;
    try {
        for (var _b = __values(children.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), index_1 = _d[0], child = _d[1];
            traverseBottomUp(operation, child, current, index_1);
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_7) throw e_7.error; }
    }
    operation(current, parent, index);
}
/**
 * Iterate all nodes and set the next node to be the only child for each node
 *
 * @param nodes query nodes to compose
 * @returns first node of argument
 */
function composeNodes() {
    var e_8, _a;
    var nodes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        nodes[_i] = arguments[_i];
    }
    var _b = __read(nodes), firstNode = _b[0], restNodes = _b.slice(1);
    var current = firstNode;
    try {
        for (var restNodes_1 = __values(restNodes), restNodes_1_1 = restNodes_1.next(); !restNodes_1_1.done; restNodes_1_1 = restNodes_1.next()) {
            var node = restNodes_1_1.value;
            current.children = [node];
            current = node;
        }
    }
    catch (e_8_1) { e_8 = { error: e_8_1 }; }
    finally {
        try {
            if (restNodes_1_1 && !restNodes_1_1.done && (_a = restNodes_1.return)) _a.call(restNodes_1);
        }
        finally { if (e_8) throw e_8.error; }
    }
    return firstNode;
}
export default QueryOptimizer;
//# sourceMappingURL=query-optimizer.js.map